import * as React from 'react';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    Link,
    MenuItem,
    Select,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import {
    getChallengeAdmin,
    updateChallengeAdmin,
    updateChallengeParticipantAdmin,
} from '../Api';
import {
    ChallengeAdminDetailDto,
    ChallengeAdminParticipantDto,
    CHALLENGE_STATE_OPTIONS,
    PARTICIPANT_STATE_OPTIONS,
    challengeStateLabel,
    participantStateLabel,
} from '../models/dtos/ChallengeAdminDto';

export default function BattlePage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [battle, setBattle] = React.useState<ChallengeAdminDetailDto | null>(null);
    const [loaded, setLoaded] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [saving, setSaving] = React.useState(false);
    const [success, setSuccess] = React.useState<string | null>(null);

    const [editState, setEditState] = React.useState<number>(1);
    const [editMoneyAccept, setEditMoneyAccept] = React.useState('');
    const [editEnterFee, setEditEnterFee] = React.useState('');
    const [editMoneyToWin, setEditMoneyToWin] = React.useState('');
    const [editIsPrivate, setEditIsPrivate] = React.useState(false);

    const [participantEdits, setParticipantEdits] = React.useState<
        Record<
            number,
            { state: number; moneyReturned: string; finalCoefficient: string; adjustBalance: boolean }
        >
    >({});

    const applyBattle = (data: ChallengeAdminDetailDto) => {
        setBattle(data);
        setEditState(data.state);
        setEditMoneyAccept(String(data.moneyAccept));
        setEditEnterFee(String(data.enterFee));
        setEditMoneyToWin(String(data.moneyToWin));
        setEditIsPrivate(data.isPrivate);
        const edits: typeof participantEdits = {};
        data.participants.forEach(p => {
            edits[p.id] = {
                state: p.state,
                moneyReturned: String(p.moneyReturned),
                finalCoefficient: String(p.finalCoefficient),
                adjustBalance: false,
            };
        });
        setParticipantEdits(edits);
    };

    const load = React.useCallback(async () => {
        if (!id) return;
        setLoaded(false);
        setError(null);
        setSuccess(null);
        try {
            const data = await getChallengeAdmin(id);
            applyBattle(data);
            setLoaded(true);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to load battle');
            setLoaded(true);
        }
    }, [id]);

    React.useEffect(() => {
        load();
    }, [load]);

    const saveChallenge = async () => {
        if (!id || !battle) return;
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            const payload: {
                state?: number;
                moneyAccept?: number;
                enterFee?: number;
                moneyToWin?: number;
                isPrivate?: boolean;
            } = {};
            if (editState !== battle.state) payload.state = editState;
            const moneyAccept = Number(editMoneyAccept);
            const enterFee = Number(editEnterFee);
            const moneyToWin = Number(editMoneyToWin);
            if (!Number.isNaN(moneyAccept) && moneyAccept !== battle.moneyAccept) {
                payload.moneyAccept = moneyAccept;
            }
            if (!Number.isNaN(enterFee) && enterFee !== battle.enterFee) {
                payload.enterFee = enterFee;
            }
            if (!Number.isNaN(moneyToWin) && moneyToWin !== battle.moneyToWin) {
                payload.moneyToWin = moneyToWin;
            }
            if (editIsPrivate !== battle.isPrivate) payload.isPrivate = editIsPrivate;

            if (Object.keys(payload).length === 0) {
                setSuccess('No challenge changes to save');
                setSaving(false);
                return;
            }
            const updated = await updateChallengeAdmin(id, payload);
            applyBattle(updated);
            setSuccess('Challenge updated');
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to update challenge');
        } finally {
            setSaving(false);
        }
    };

    const saveParticipant = async (participant: ChallengeAdminParticipantDto) => {
        if (!id) return;
        const edit = participantEdits[participant.id];
        if (!edit) return;
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            const payload: {
                state?: number;
                moneyReturned?: number;
                finalCoefficient?: number;
                adjustBalance?: boolean;
            } = {};
            if (edit.state !== participant.state) payload.state = edit.state;
            const moneyReturned = Number(edit.moneyReturned);
            const finalCoefficient = Number(edit.finalCoefficient);
            if (!Number.isNaN(moneyReturned) && moneyReturned !== participant.moneyReturned) {
                payload.moneyReturned = moneyReturned;
                payload.adjustBalance = edit.adjustBalance;
            }
            if (!Number.isNaN(finalCoefficient) && finalCoefficient !== participant.finalCoefficient) {
                payload.finalCoefficient = finalCoefficient;
            }
            if (Object.keys(payload).length === 0 || (Object.keys(payload).length === 1 && payload.adjustBalance != null)) {
                setSuccess('No participant changes to save');
                setSaving(false);
                return;
            }
            const updated = await updateChallengeParticipantAdmin(id, participant.id, payload);
            applyBattle(updated);
            setSuccess(`Participant #${participant.id} updated`);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to update participant');
        } finally {
            setSaving(false);
        }
    };

    if (!loaded) {
        return (
            <Stack alignItems="center" sx={{ my: 8 }}>
                <CircularProgress size={80} />
            </Stack>
        );
    }

    if (!battle) {
        return (
            <Stack sx={{ mx: 5, my: 5 }} gap={2}>
                <Alert severity="error">{error ?? 'Battle not found'}</Alert>
                <Button onClick={() => navigate('/battles')}>Back to list</Button>
            </Stack>
        );
    }

    return (
        <Stack gap={3} sx={{ mx: 5, my: 5, maxWidth: 1100 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h4">Battle #{battle.id}</Typography>
                <Button variant="outlined" onClick={() => navigate('/battles')}>
                    Back to list
                </Button>
            </Stack>

            {error ? <Alert severity="error">{error}</Alert> : null}
            {success ? <Alert severity="success">{success}</Alert> : null}

            <Box>
                <Typography variant="h6" gutterBottom>
                    Overview
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Created: {battle.createdAt ? new Date(battle.createdAt).toLocaleString() : '—'} · Current
                    state: {challengeStateLabel(battle.state)} · Version: {battle.version}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Match:{' '}
                    {battle.match ? (
                        <Link
                            component="button"
                            type="button"
                            underline="hover"
                            onClick={() => navigate('/matches/' + battle.match!.matchId)}
                        >
                            #{battle.match.matchId} {battle.match.homeTeamName ?? '?'} vs{' '}
                            {battle.match.awayTeamName ?? '?'}
                        </Link>
                    ) : (
                        '—'
                    )}
                    {battle.match?.startDate
                        ? ` · ${new Date(battle.match.startDate).toLocaleString()}`
                        : ''}
                    {battle.match?.scoreHome != null && battle.match?.scoreAway != null
                        ? ` · Score ${battle.match.scoreHome} - ${battle.match.scoreAway}`
                        : ''}
                </Typography>
                {battle.match ? (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        Team names open the match page (no dedicated team page in dashboard).
                    </Typography>
                ) : null}
            </Box>

            <Divider />

            <Box>
                <Typography variant="h6" gutterBottom>
                    Edit challenge
                </Typography>
                <Grid container spacing={2} maxWidth={700}>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel>State</InputLabel>
                            <Select
                                label="State"
                                value={editState}
                                onChange={e => setEditState(Number(e.target.value))}
                            >
                                {CHALLENGE_STATE_OPTIONS.map(o => (
                                    <MenuItem key={o.value} value={o.value}>
                                        {o.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={editIsPrivate}
                                    onChange={e => setEditIsPrivate(e.target.checked)}
                                />
                            }
                            label="Private"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Stake (moneyAccept)"
                            size="small"
                            fullWidth
                            value={editMoneyAccept}
                            onChange={e => setEditMoneyAccept(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Enter fee"
                            size="small"
                            fullWidth
                            value={editEnterFee}
                            onChange={e => setEditEnterFee(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Prize (moneyToWin)"
                            size="small"
                            fullWidth
                            value={editMoneyToWin}
                            onChange={e => setEditMoneyToWin(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Button variant="contained" disabled={saving} onClick={saveChallenge}>
                            Save challenge
                        </Button>
                    </Grid>
                </Grid>
            </Box>

            <Divider />

            <Box>
                <Typography variant="h6" gutterBottom>
                    Participants
                </Typography>
                <Stack gap={3}>
                    {battle.participants.map(p => {
                        const edit = participantEdits[p.id];
                        if (!edit) return null;
                        return (
                            <Box
                                key={p.id}
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    p: 2,
                                }}
                            >
                                <Stack direction="row" gap={2} alignItems="center" mb={2}>
                                    <Avatar
                                        src={p.userPhotoUrl ?? undefined}
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => navigate('/users/' + p.userId)}
                                    />
                                    <Box>
                                        <Typography fontWeight={600}>
                                            <Link
                                                component="button"
                                                type="button"
                                                underline="hover"
                                                onClick={() => navigate('/users/' + p.userId)}
                                            >
                                                {p.userName ?? 'User'} #{p.userId}
                                            </Link>
                                            {p.isCreator ? ' (creator)' : ''}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Current: {participantStateLabel(p.state)} · Returned:{' '}
                                            {p.moneyReturned} · Coef: {p.finalCoefficient}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Bet: {p.betLabel ?? p.betTypeName ?? '—'}
                                            {p.placedBetId != null ? ` (#${p.placedBetId})` : ''}
                                            {p.placedBetCoefficient != null
                                                ? ` · placed ${p.placedBetCoefficient}`
                                                : ''}
                                        </Typography>
                                        {(p.varietyName || p.betGroupName) && (
                                            <Typography variant="body2" color="text.secondary">
                                                {p.varietyName ? `Variety: ${p.varietyName}` : ''}
                                                {p.varietyName && p.betGroupName ? ' · ' : ''}
                                                {p.betGroupName ? `Group: ${p.betGroupName}` : ''}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Button
                                        size="small"
                                        sx={{ ml: 'auto' }}
                                        onClick={() => navigate('/users/' + p.userId)}
                                    >
                                        Open user
                                    </Button>
                                </Stack>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>State</InputLabel>
                                            <Select
                                                label="State"
                                                value={edit.state}
                                                onChange={e =>
                                                    setParticipantEdits(prev => ({
                                                        ...prev,
                                                        [p.id]: {
                                                            ...prev[p.id],
                                                            state: Number(e.target.value),
                                                        },
                                                    }))
                                                }
                                            >
                                                {PARTICIPANT_STATE_OPTIONS.map(o => (
                                                    <MenuItem key={o.value} value={o.value}>
                                                        {o.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            label="Money returned"
                                            size="small"
                                            fullWidth
                                            value={edit.moneyReturned}
                                            onChange={e =>
                                                setParticipantEdits(prev => ({
                                                    ...prev,
                                                    [p.id]: {
                                                        ...prev[p.id],
                                                        moneyReturned: e.target.value,
                                                    },
                                                }))
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            label="Final coefficient"
                                            size="small"
                                            fullWidth
                                            value={edit.finalCoefficient}
                                            onChange={e =>
                                                setParticipantEdits(prev => ({
                                                    ...prev,
                                                    [p.id]: {
                                                        ...prev[p.id],
                                                        finalCoefficient: e.target.value,
                                                    },
                                                }))
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={edit.adjustBalance}
                                                    onChange={e =>
                                                        setParticipantEdits(prev => ({
                                                            ...prev,
                                                            [p.id]: {
                                                                ...prev[p.id],
                                                                adjustBalance: e.target.checked,
                                                            },
                                                        }))
                                                    }
                                                />
                                            }
                                            label="Also adjust user balance by moneyReturned delta"
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Button
                                            variant="contained"
                                            disabled={saving}
                                            onClick={() => saveParticipant(p)}
                                        >
                                            Save participant
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Box>
                        );
                    })}
                </Stack>
            </Box>
        </Stack>
    );
}
